import { Mediator, app, payload, eData, EventData } from "modular-openscriptjs";
import {
	eParse,
	inform,
	processStart,
	processEnd,
	empty,
	oneOf,
	approved,
	validPhone,
} from "../helpers";
import { STRINGS, analytics, api, gc, uc, dc } from "../declarations";
import { routes } from "../backendRoutes";
import { analyze } from "../analytics";

const $e = app("$e");
const broker = app("broker");

export class Order extends Mediator {
	$$order = {
		needs: {
			display: (ed, event) =>
				this.trackOrder(eParse(ed).message.get("orderId", 0)),
			placement: (ed, event) => {
				this.placeOrder(ed, event);
			},
			recentOrders: (ed, event) => this.getRecent(ed),
			repeat: (ed, event) => this.copyOrder(ed, event),
			history: (ed, event) => {
				this.getOrders(ed, event);
			},
			cancellation: (ed, event) => {
				this.broadcast($e.order.needs.cancellationReason, ed);
			},
			payment: (ed, event) => {
				this.getPayment(ed, event);
			},
			trackInformation: (ed, event) => {
				this.updatedTrackedOrder(ed, event);
			},
			paymentRefund: (ed, event) => {
				this.refundPayment(ed, event);
			},
			deletion: (ed, event) => {
				this.deleteOrder(ed, event);
			},
		},

		has: {
			cancellationReason: (ed, event) => {
				this.cancelOrder(ed, event);
			},
		},

		placed: (ed, event) => {
			this.send($e.order.needs.recentOrders);
			this.resetOrderHistory();
		},

		cancelled_paymentRefunded_deleted_surplusClaimed: (ed, event) => {
			this.send($e.order.needs.trackInformation, ed);
		},
	};

	$$user = {
		authenticated: (ed, event) => {
			this.send($e.order.needs.recentOrders);
			this.checkChatOrders();
		},
		loggedOut: () => {
			this.resetOrderHistory();
			uc.orders.value = [];
		},
	};

	$$payment = {
		completed: (ed, event) => {
			let data = eParse(ed);

			if ("order" != data.meta.get("type")) return;

			if (data.meta.get("initiator") == "deliverer") {
				return;
			}

			inform("Payment Completed for your order", STRINGS.SUCCESS);

			let reqAmount = app("router").params.amount;
			let paidAmount = data.message.get("amount");

			if (reqAmount == paidAmount) {
				analytics.orderConfirmed(data.meta.get("typeId"));
				this.send($e.user.needs.information);
			} else {
				analyze("order_partial_payment", {
					amount: paidAmount,
					currency: dc.userCurrency,
				});
			}

			this.broadcast(
				$e.order.needs.display,
				payload({ orderId: data.meta.get("typeId") })
			);
		},
	};

	$$chatInitiatedOrder = {
		has: {
			confirmation: (ed, event) => this.confirmChatOrder(ed, event),
		},
	};

	async placeOrder(ed, event) {
		if (!validPhone()) {
			this.broadcast($e.order.has.placementError, ed);
			this.broadcast($e.user.needs.quickUpdate);

			inform("Your phone number is required");

			return;
		}

		if (uc.addresses.length < 1) {
			inform("Please choose a delivery Address", STRINGS.ERROR);
			return this.broadcast($e.order.has.placementError, ed);
		}

		if (Object.entries(uc.cart.value).length < 1) {
			inform("Your Cart is Empty", STRINGS.ERROR);
			return this.broadcast($e.order.has.placementError, ed);
		}

		if (
			uc.deliveryFee &&
			Number(uc.deliveryFee) < minDeliveryFee
		) {
			inform(
				`Your chosen delivery fee cannot be less than ${minDeliveryFee}`,
				STRINGS.ERROR
			);
			return this.broadcast($e.order.has.placementError, ed);
		}

		processStart({ title: "Placing order" });

        const cartItems = Object.values(uc.cart.value).map(item => ({
            id: item.id,
            qty: item.qty,
            price: item.price
        }));

		let res = await api.post(routes.orders, {
			delivery_address_id:
				uc.addresses[uc.addresses.length - 1]?.id,
			company_id: uc.logisticsCompany.value?.id,
			notes: uc.orderNotes,
			fleet_type: uc.fleetType.value,
            items: cartItems,
			schedule_date: uc.scheduleDate.value || null,
			schedule_time: uc.scheduleTime.value || null,
		});

		if (!res) {
			inform("An error occurred while placing your order", STRINGS.ERROR);
			processEnd();
			return this.broadcast($e.order.has.placementError, ed);
		}

		uc.deliveryFee = 0;
		uc.orderNotes = "";
		uc.scheduleDate.value = "";
		uc.scheduleTime.value = "";
        localStorage.removeItem("carata-cart");
        uc.cart.value = {};
        uc.cartMap.value = {};

		inform(
			"Your order was placed successfully. Redirecting to payment",
			STRINGS.SUCCESS
		);

		let data = eParse(ed);
		data.message.orderId = res.data.orderId;

		processEnd();

		this.broadcast($e.order.placed, payload(data.message, data.meta));

		analyze("place_order", {
			amount: res.data.amount,
			currency: res.data.currency,
			items_count: Object.keys(uc.cart.value).length,
		});

		this.broadcast(
			$e.order.needs.payment,
			payload({
				orderId: res.data.orderId,
				amount: res.data.amount,
				currency: res.data.currency,
			})
		);
	}

	async getPayment(ed, event) {
		let data = eParse(ed);

		if (data.message.get("orderId", 0) == 0) {
			inform("Invalid order ID", STRINGS.ERROR);

			return;
		}

		let orderId = data.message.get("orderId");

		data.meta.put("route", `${routes.orders}/${orderId}/pay`);
		data.meta.put("editable", true);
		data.meta.put("type", "order");
		data.meta.put("typeId", orderId);
		data.meta.put("initiator", data.message.get("initiator", "customer"));

		this.broadcast(
			$e.payment.needs.collection,
			payload(data.message, data.meta)
		);
	}

	async refundPayment(ed, event) {
		if (!approved(event, ed, "You want to refund this payment manually?"))
			return;

		const { message } = eParse(ed);

		processStart();

		let res = await api.put(
			`${routes.orders}/${message.get("orderId")}/payments/${message.get(
				"paymentId"
			)}/refund`
		);

		processEnd();

		if (!res) return;

		inform(res.message, STRINGS.SUCCESS);

		this.send($e.order.paymentRefunded, ed);
	}

	async trackOrder(orderId) {
		if (orderId == 0) app("router").to("/");
		app("router").to(`${routes.orders}/${orderId}/track`);
	}

	async updatedTrackedOrder(ed, event) {
		let orderId = eParse(ed).message.get("orderId");

		processStart({ title: "Getting order information" });

		let res = await api.get(`${routes.orders}/${orderId}`);
		processEnd();

		if (!res) {
			inform("Order not found", STRINGS.ERROR);
			return app("router").to("/404");
		}

		dc.orderTracked.value = res.data.order;
	}

	async getRecent(ed) {
		if (!gc.auth.value) {
			uc.orders.value = [];
			return;
		}

		let res = await api.get(
			`${routes.orders}/recent`,
			{ page: 1, interval: 10 },
			{ silent: true }
		);

		if (!res) {
			uc.orders.value = [];
			return;
		}

		uc.orders.value = res.data.orders.data;
		broker.broadcast($e.order.has.recentOrders);
	}

	async copyOrder(ed, event) {
		if (!approved(event, ed, "You want to re-place this order")) return;
		this.broadcast($e.order.needs.repeatForm, ed);
	}

	async getOrders(ed, event) {
		ed = oneOf(ed, eData());
		let data = new EventData();

		try {
			data = eParse(ed);
		} catch (e) {}

		let message = data.message;

		if (!("page" in message)) message = dc.orderHistoryPageInfo;

		if (dc?.orderHistoryPageInfo?.lastPage === true) {
			return this.broadcast($e.order.has.completeHistory);
		}

		this.broadcast(
			$e.app.indicator.process.start,
			eData({}, { title: "Fetching order history" })
		);

		let res = await api.get(`${routes.orders}`, message);

		this.broadcast($e.app.indicator.process.end);

		if (!res) {
			uc.orderHistory.value = [];
			return;
		}

		uc.orderHistory.push(...res.data.orders.data);

		if (res.data.orders.last_page == res.data.orders.current_page) {
			dc.orderHistoryPageInfo.lastPage = true;

			this.broadcast($e.order.has.completeHistory);
			inform("All orders displayed");
		}
	}

	async resetOrderHistory() {
		dc.orderHistoryPageInfo = {
			page: 1,
			interval: 15,
			lastPage: false,
		};
		uc.orderHistory.value = [];
	}

	async cancelOrder(ed, event) {
		let data = eParse(ed);

		if (!approved(event, ed, "You want to cancel this order")) return;

		processStart();
		let res = await api.put(`${routes.orders}/${data.message.id}/cancel`, {
			reasons: data.message.get("reasons", ""),
			feedback: data.message.get("feedback", ""),
		});
		processEnd();

		if (!res) {
			return this.send($e.order.has.cancellationError, ed);
		}

		inform(res.message, STRINGS.SUCCESS);
		this.broadcast(
			$e.order.cancelled,
			payload({ orderId: data.message.get("id") }, data.meta)
		);

		analytics.orderCancelled(data.message.get("id"));
	}

	async deleteOrder(ed, event) {
		let data = eParse(ed);

		if (!approved(event, ed, "You want to delete this order")) return;

		processStart();
		let res = await api.delete(`${routes.orders}/${data.message.id}`);
		processEnd();

		if (!res) {
			return this.send($e.order.has.deletionError, ed);
		}

		inform(res.message, STRINGS.SUCCESS);
		this.broadcast(
			$e.order.deleted,
			payload({ orderId: data.message.get("id") }, data.meta)
		);
	}

	async checkChatOrders() {
		let res = await api.get(
			"/chat-initiated-orders/unconfirmed",
			{},
			{ silent: true }
		);
		if (!res || empty(res.data?.pendingOrder)) return;

		let order = res.data.pendingOrder;

		this.send(
			$e.chatInitiatedOrder.needs.confirmationForm,
			payload({ pendingOrder: order })
		);
	}

	async confirmChatOrder(ed, event) {
		let data = eParse(ed);

		let orderId = data.message.get("orderId");
		let status = data.message.get("status");

		let res = await api.post(
			`/chat-initiated-orders/${orderId}/confirmation`,
			{ status }
		);

		if (!res) {
			return this.send($e.chatInitiatedOrder.has.responseError, ed);
		}

		inform(res.message);

		return this.send($e.chatInitiatedOrder.is.confirmed, ed);
	}
}
